import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3Icon, UsersIcon, ClockIcon, Share2Icon, WrenchIcon, CheckCircle2Icon, AlertCircleIcon, CalendarIcon, TrophyIcon, ActivityIcon, SmartphoneIcon, ChevronDownIcon } from 'lucide-react';
import { SectionHeading } from '../components/ui/SectionHeading';
import { ScrollReveal } from '../components/ui/ScrollReveal';
export function PlayAnalyticsPage() {
  const [dateRange, setDateRange] = useState('This Month');
  const keyMetrics = [{
    label: 'Total Rounds Played',
    value: '1,247',
    trend: '+18%',
    isPositive: true,
    icon: ActivityIcon
  }, {
    label: 'Unique Players',
    value: '892',
    trend: '+12%',
    isPositive: true,
    icon: UsersIcon
  }, {
    label: 'Avg. Round Time',
    value: '22m',
    trend: '-3m',
    isPositive: true,
    icon: ClockIcon
  }, {
    label: 'Social Shares',
    value: '486',
    trend: '+34%',
    isPositive: true,
    icon: Share2Icon
  }];
  const maintenanceTasks = [{
    task: 'Surface brushing',
    status: 'completed',
    date: 'Mar 12'
  }, {
    task: 'Hole cup replacement',
    status: 'completed',
    date: 'Mar 10'
  }, {
    task: 'Seam inspection',
    status: 'pending',
    date: 'Due Mar 19'
  }, {
    task: 'Deep clean',
    status: 'pending',
    date: 'Due Mar 25'
  }, {
    task: 'QR code check',
    status: 'pending',
    date: 'Due Mar 19'
  }];
  const recentActivity = [{
    action: 'Group of 4 finished round',
    detail: 'Winner: Sarah K. (Score: 21)',
    time: '10 mins ago',
    type: 'play'
  }, {
    action: 'Maintenance Logged',
    detail: 'Surface brushing completed by Team A',
    time: '2 hours ago',
    type: 'maintenance'
  }, {
    action: 'New Course Record!',
    detail: 'Alex M. scored 18 (Stroke Play)',
    time: '5 hours ago',
    type: 'record'
  }, {
    action: 'Result shared to Instagram',
    detail: 'By: Alex M.',
    time: '5 hours ago',
    type: 'social'
  }, {
    action: 'Group of 2 started round',
    detail: 'Mode: Match Play',
    time: '6 hours ago',
    type: 'play'
  }, {
    action: 'Equipment Alert',
    detail: 'Putter inventory low (2 need grip replacement)',
    time: '1 day ago',
    type: 'alert'
  }];
  return <div className="w-full bg-brand-cream-light min-h-screen pb-24" data-id="element-717">
      {/* Hero Header */}
      <section className="pt-32 pb-20 bg-brand-green-deep text-brand-cream-light relative overflow-hidden" data-id="element-718">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(184,150,90,0.15)_0%,transparent_60%)]" data-id="element-719" />

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-720">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8" data-id="element-721">
            <div data-id="element-722">
              <span className="text-brand-gold text-sm font-bold uppercase tracking-widest mb-4 block" data-id="element-723">
                Venue Dashboard
              </span>
              <h1 className="text-4xl md:text-5xl font-heading mb-4" data-id="element-724">
                PuttLab Play Analytics
              </h1>
              <p className="text-brand-cream/70 font-body text-lg max-w-xl" data-id="element-725">
                Track engagement, monitor performance, and manage your venue
                maintenance in real-time.
              </p>
            </div>

            <div className="flex flex-col gap-4" data-id="element-726">
              {/* Venue Selector Mock */}
              <button className="flex items-center justify-between bg-brand-cream/10 border border-brand-cream/20 rounded-lg px-4 py-3 min-w-[280px] hover:bg-brand-cream/20 transition-colors" data-id="element-727">
                <div className="text-left" data-id="element-728">
                  <span className="block text-[10px] text-brand-cream/50 uppercase tracking-wider" data-id="element-729">
                    Current Venue
                  </span>
                  <span className="block font-body text-sm font-medium" data-id="element-730">
                    The Sunset Green — Fern Resort, Orillia
                  </span>
                </div>
                <ChevronDownIcon className="w-4 h-4 text-brand-gold" data-id="element-731" />
              </button>

              {/* Date Range Tabs */}
              <div className="flex bg-brand-charcoal/50 p-1 rounded-lg border border-brand-cream/10" data-id="element-732">
                {['Today', 'This Week', 'This Month', 'All Time'].map(range => <button key={range} onClick={() => setDateRange(range)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${dateRange === range ? 'bg-brand-gold text-brand-charcoal shadow-sm' : 'text-brand-cream/50 hover:text-brand-cream-light'}`} data-id="element-733">
                      {range}
                    </button>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 -mt-8 relative z-20" data-id="element-734">
        {/* Key Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-735">
          {keyMetrics.map((metric, i) => <ScrollReveal key={metric.label} delay={i * 0.1} data-id="element-736">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-brand-stone/10" data-id="element-737">
                <div className="flex items-center justify-between mb-4" data-id="element-738">
                  <div className="p-3 bg-brand-cream rounded-xl" data-id="element-739">
                    <metric.icon className="w-5 h-5 text-brand-green" data-id="element-740" />
                  </div>
                  <span className={`text-sm font-bold flex items-center ${metric.isPositive ? 'text-green-600' : 'text-red-500'}`} data-id="element-741">
                    {metric.trend}
                  </span>
                </div>
                <h3 className="text-3xl font-heading text-brand-green-deep mb-1" data-id="element-742">
                  {metric.value}
                </h3>
                <p className="text-xs font-body text-brand-stone uppercase tracking-wider" data-id="element-743">
                  {metric.label}
                </p>
              </div>
            </ScrollReveal>)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12" data-id="element-744">
          {/* Game Mode Breakdown */}
          <ScrollReveal className="lg:col-span-1" data-id="element-745">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-stone/10 h-full" data-id="element-746">
              <h3 className="text-xl font-heading text-brand-green-deep mb-6" data-id="element-747">
                Game Modes
              </h3>

              {/* Mock Donut Chart */}
              <div className="relative w-48 h-48 mx-auto mb-8" data-id="element-748">
                <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90" data-id="element-749">
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#F5F0E8" strokeWidth="20" data-id="element-750" />
                  {/* Stroke Play 45% */}
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#1B3A2D" strokeWidth="20" strokeDasharray="113 251" strokeDashoffset="0" data-id="element-751" />
                  {/* Match Play 25% */}
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#B8965A" strokeWidth="20" strokeDasharray="63 251" strokeDashoffset="-113" data-id="element-752" />
                  {/* Challenge 20% */}
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#C4A97D" strokeWidth="20" strokeDasharray="50 251" strokeDashoffset="-176" data-id="element-753" />
                  {/* Best Ball 10% */}
                  <circle cx="50" cy="50" r="40" fill="transparent" stroke="#8A8578" strokeWidth="20" strokeDasharray="25 251" strokeDashoffset="-226" data-id="element-754" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-755">
                  <span className="text-3xl font-heading text-brand-green-deep" data-id="element-756">
                    1.2k
                  </span>
                  <span className="text-[10px] text-brand-stone uppercase tracking-widest" data-id="element-757">
                    Rounds
                  </span>
                </div>
              </div>

              <div className="space-y-4" data-id="element-758">
                {[{
                name: 'Stroke Play',
                pct: '45%',
                count: '562',
                color: 'bg-brand-green'
              }, {
                name: 'Match Play',
                pct: '25%',
                count: '312',
                color: 'bg-brand-gold'
              }, {
                name: 'Resort Challenge',
                pct: '20%',
                count: '249',
                color: 'bg-brand-sand'
              }, {
                name: 'Best Ball',
                pct: '10%',
                count: '124',
                color: 'bg-brand-stone'
              }].map(mode => <div key={mode.name} className="flex items-center justify-between text-sm" data-id="element-759">
                    <div className="flex items-center space-x-2" data-id="element-760">
                      <div className={`w-3 h-3 rounded-full ${mode.color}`} data-id="element-761" />
                      <span className="font-medium text-brand-charcoal" data-id="element-762">
                        {mode.name}
                      </span>
                    </div>
                    <div className="text-right" data-id="element-763">
                      <span className="font-bold text-brand-green-deep mr-2" data-id="element-764">
                        {mode.pct}
                      </span>
                      <span className="text-brand-stone text-xs" data-id="element-765">
                        ({mode.count})
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </ScrollReveal>

          {/* Weekly Activity Chart */}
          <ScrollReveal className="lg:col-span-2" delay={0.2} data-id="element-766">
            <div className="bg-brand-green-deep rounded-2xl p-8 shadow-lg h-full flex flex-col" data-id="element-767">
              <div className="flex justify-between items-center mb-8" data-id="element-768">
                <div data-id="element-769">
                  <h3 className="text-xl font-heading text-brand-cream-light mb-1" data-id="element-770">
                    Weekly Activity
                  </h3>
                  <p className="text-sm text-brand-cream/60 font-body" data-id="element-771">
                    Rounds played over the last 7 days
                  </p>
                </div>
                <div className="p-3 bg-brand-cream/10 rounded-xl" data-id="element-772">
                  <BarChart3Icon className="w-5 h-5 text-brand-gold" data-id="element-773" />
                </div>
              </div>

              {/* Bar Chart Mockup */}
              <div className="flex-1 flex items-end justify-between gap-2 md:gap-4 pt-8" data-id="element-774">
                {[{
                day: 'Mon',
                val: 32,
                height: '40%'
              }, {
                day: 'Tue',
                val: 28,
                height: '35%'
              }, {
                day: 'Wed',
                val: 45,
                height: '55%'
              }, {
                day: 'Thu',
                val: 38,
                height: '45%'
              }, {
                day: 'Fri',
                val: 52,
                height: '65%'
              }, {
                day: 'Sat',
                val: 78,
                height: '100%'
              }, {
                day: 'Sun',
                val: 65,
                height: '85%'
              }].map((d, i) => <div key={d.day} className="flex flex-col items-center flex-1 group" data-id="element-775">
                    <div className="w-full relative flex justify-center h-48 md:h-64 items-end" data-id="element-776">
                      <motion.div initial={{
                    height: 0
                  }} whileInView={{
                    height: d.height
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: i * 0.1,
                    type: 'spring'
                  }} className="w-full max-w-[40px] bg-brand-gold rounded-t-md relative group-hover:bg-brand-gold-light transition-colors" data-id="element-777">
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-brand-charcoal text-brand-cream-light text-xs py-1 px-2 rounded font-bold" data-id="element-778">
                          {d.val}
                        </div>
                      </motion.div>
                    </div>
                    <span className="text-brand-cream/60 text-xs font-bold uppercase tracking-widest mt-4" data-id="element-779">
                      {d.day}
                    </span>
                  </div>)}
              </div>
            </div>
          </ScrollReveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12" data-id="element-780">
          {/* Maintenance & Venue Health */}
          <ScrollReveal data-id="element-781">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-stone/10 h-full" data-id="element-782">
              <div className="flex justify-between items-start mb-8" data-id="element-783">
                <div data-id="element-784">
                  <h3 className="text-xl font-heading text-brand-green-deep mb-1" data-id="element-785">
                    Maintenance & Health
                  </h3>
                  <p className="text-sm text-brand-stone font-body" data-id="element-786">
                    Keep your venue in premium condition
                  </p>
                </div>
                <div className="flex items-center space-x-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-full border border-green-200" data-id="element-787">
                  <CheckCircle2Icon className="w-4 h-4" data-id="element-788" />
                  <span className="text-xs font-bold uppercase tracking-wider" data-id="element-789">
                    Excellent
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-790">
                <div className="bg-brand-cream p-4 rounded-xl" data-id="element-791">
                  <span className="text-[10px] text-brand-stone uppercase tracking-widest block mb-1" data-id="element-792">
                    Last Maintenance
                  </span>
                  <span className="font-bold text-brand-green-deep" data-id="element-793">
                    March 12, 2026
                  </span>
                </div>
                <div className="bg-brand-cream p-4 rounded-xl" data-id="element-794">
                  <span className="text-[10px] text-brand-stone uppercase tracking-widest block mb-1" data-id="element-795">
                    Next Scheduled
                  </span>
                  <span className="font-bold text-brand-gold" data-id="element-796">
                    March 19, 2026
                  </span>
                </div>
              </div>

              <h4 className="text-sm font-bold text-brand-charcoal uppercase tracking-wider mb-4" data-id="element-797">
                Task Checklist
              </h4>
              <div className="space-y-3 mb-8" data-id="element-798">
                {maintenanceTasks.map((task, i) => <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-brand-stone/10 bg-gray-50/50" data-id="element-799">
                    <div className="flex items-center space-x-3" data-id="element-800">
                      {task.status === 'completed' ? <CheckCircle2Icon className="w-5 h-5 text-green-500" data-id="element-801" /> : <div className="w-5 h-5 rounded-full border-2 border-brand-gold/50 flex items-center justify-center" data-id="element-802">
                          <div className="w-2 h-2 bg-brand-gold rounded-full" data-id="element-803" />
                        </div>}
                      <span className={`text-sm font-medium ${task.status === 'completed' ? 'text-brand-stone line-through' : 'text-brand-charcoal'}`} data-id="element-804">
                        {task.task}
                      </span>
                    </div>
                    <span className="text-xs text-brand-stone" data-id="element-805">
                      {task.date}
                    </span>
                  </div>)}
              </div>

              <h4 className="text-sm font-bold text-brand-charcoal uppercase tracking-wider mb-4" data-id="element-806">
                Equipment Status
              </h4>
              <div className="grid grid-cols-2 gap-3" data-id="element-807">
                <div className="p-3 rounded-lg border border-red-200 bg-red-50" data-id="element-808">
                  <span className="text-xs font-bold text-red-700 block mb-1" data-id="element-809">
                    Putters
                  </span>
                  <span className="text-xs text-red-600" data-id="element-810">
                    12 available, 2 need grip replacement
                  </span>
                </div>
                <div className="p-3 rounded-lg border border-green-200 bg-green-50" data-id="element-811">
                  <span className="text-xs font-bold text-green-700 block mb-1" data-id="element-812">
                    Scorecards/QR
                  </span>
                  <span className="text-xs text-green-600" data-id="element-813">All functional</span>
                </div>
                <div className="p-3 rounded-lg border border-green-200 bg-green-50" data-id="element-814">
                  <span className="text-xs font-bold text-green-700 block mb-1" data-id="element-815">
                    Lighting
                  </span>
                  <span className="text-xs text-green-600" data-id="element-816">
                    Operational (check Apr 1)
                  </span>
                </div>
                <div className="p-3 rounded-lg border border-green-200 bg-green-50" data-id="element-817">
                  <span className="text-xs font-bold text-green-700 block mb-1" data-id="element-818">
                    Hole Cups
                  </span>
                  <span className="text-xs text-green-600" data-id="element-819">
                    Replaced Mar 10
                  </span>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Player Engagement & Social */}
          <div className="space-y-8" data-id="element-820">
            <ScrollReveal delay={0.2} data-id="element-821">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-stone/10" data-id="element-822">
                <h3 className="text-xl font-heading text-brand-green-deep mb-6" data-id="element-823">
                  Player Engagement
                </h3>

                <div className="grid grid-cols-2 gap-6 mb-6" data-id="element-824">
                  <div data-id="element-825">
                    <span className="text-[10px] text-brand-stone uppercase tracking-widest block mb-1" data-id="element-826">
                      Avg Group Size
                    </span>
                    <span className="text-3xl font-heading text-brand-gold" data-id="element-827">
                      2.8{' '}
                      <span className="text-sm font-body text-brand-stone" data-id="element-828">
                        players
                      </span>
                    </span>
                  </div>
                  <div data-id="element-829">
                    <span className="text-[10px] text-brand-stone uppercase tracking-widest block mb-1" data-id="element-830">
                      Return Rate (7d)
                    </span>
                    <span className="text-3xl font-heading text-brand-gold" data-id="element-831">
                      34%
                    </span>
                  </div>
                </div>

                <div className="mb-2" data-id="element-832">
                  <span className="text-xs font-bold text-brand-charcoal uppercase tracking-wider block mb-3" data-id="element-833">
                    Peak Hours Heatmap
                  </span>
                  <div className="flex h-8 rounded-lg overflow-hidden" data-id="element-834">
                    <div className="flex-1 bg-brand-stone/20" title="8am-10am (Low)" data-id="element-835" />
                    <div className="flex-[2] bg-brand-sand/60" title="10am-2pm (Moderate)" data-id="element-836" />
                    <div className="flex-[3] bg-brand-gold" title="2pm-5pm (Peak)" data-id="element-837" />
                    <div className="flex-[2] bg-brand-sand/60" title="5pm-7pm (Moderate)" data-id="element-838" />
                    <div className="flex-[3] bg-brand-stone/20" title="7pm-10pm (Low)" data-id="element-839" />
                  </div>
                  <div className="flex justify-between text-[10px] text-brand-stone mt-1" data-id="element-840">
                    <span data-id="element-841">8am</span>
                    <span data-id="element-842">12pm</span>
                    <span data-id="element-843">4pm</span>
                    <span data-id="element-844">8pm</span>
                    <span data-id="element-845">10pm</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-846">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-stone/10" data-id="element-847">
                <div className="flex justify-between items-center mb-6" data-id="element-848">
                  <h3 className="text-xl font-heading text-brand-green-deep" data-id="element-849">
                    Social & Content
                  </h3>
                  <div className="p-2 bg-pink-50 rounded-lg" data-id="element-850">
                    <InstagramIcon className="w-5 h-5 text-pink-600" data-id="element-851" />
                  </div>
                </div>

                <div className="space-y-4" data-id="element-852">
                  <div className="flex justify-between items-center p-3 bg-brand-cream rounded-xl" data-id="element-853">
                    <span className="text-sm font-medium text-brand-charcoal" data-id="element-854">
                      Share Conversion Rate
                    </span>
                    <span className="font-bold text-brand-green-deep" data-id="element-855">39%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-brand-cream rounded-xl" data-id="element-856">
                    <span className="text-sm font-medium text-brand-charcoal" data-id="element-857">
                      Top Sharing Day
                    </span>
                    <span className="font-bold text-brand-green-deep" data-id="element-858">
                      Saturday (avg 18)
                    </span>
                  </div>
                  <div className="p-4 border border-brand-stone/20 rounded-xl" data-id="element-859">
                    <span className="text-[10px] text-brand-stone uppercase tracking-widest block mb-2" data-id="element-860">
                      Most Shared Result
                    </span>
                    <div className="flex items-center space-x-3" data-id="element-861">
                      <div className="w-10 h-10 bg-brand-gold/20 rounded-full flex items-center justify-center" data-id="element-862">
                        <TrophyIcon className="w-5 h-5 text-brand-gold" data-id="element-863" />
                      </div>
                      <div data-id="element-864">
                        <p className="text-sm font-bold text-brand-charcoal" data-id="element-865">
                          Alex M. Course Record
                        </p>
                        <p className="text-xs text-brand-stone" data-id="element-866">
                          Shared 12 times this week
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>

        {/* Recent Activity Feed */}
        <ScrollReveal data-id="element-867">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-brand-stone/10" data-id="element-868">
            <h3 className="text-xl font-heading text-brand-green-deep mb-6" data-id="element-869">
              Recent Activity Log
            </h3>

            <div className="space-y-0" data-id="element-870">
              {recentActivity.map((activity, i) => {
              let Icon = ActivityIcon;
              let colorClass = 'text-brand-green bg-brand-green/10';
              if (activity.type === 'maintenance') {
                Icon = WrenchIcon;
                colorClass = 'text-blue-600 bg-blue-50';
              }
              if (activity.type === 'record') {
                Icon = TrophyIcon;
                colorClass = 'text-brand-gold bg-brand-gold/10';
              }
              if (activity.type === 'social') {
                Icon = Share2Icon;
                colorClass = 'text-pink-600 bg-pink-50';
              }
              if (activity.type === 'alert') {
                Icon = AlertCircleIcon;
                colorClass = 'text-red-600 bg-red-50';
              }
              return <div key={i} className="flex items-start space-x-4 p-4 border-b border-brand-stone/10 last:border-0 hover:bg-gray-50 transition-colors rounded-lg" data-id="element-871">
                    <div className={`p-2 rounded-lg ${colorClass} shrink-0 mt-1`} data-id="element-872">
                      <Icon className="w-4 h-4" data-id="element-873" />
                    </div>
                    <div className="flex-1" data-id="element-874">
                      <div className="flex justify-between items-start mb-1" data-id="element-875">
                        <p className="text-sm font-bold text-brand-charcoal" data-id="element-876">
                          {activity.action}
                        </p>
                        <span className="text-[10px] text-brand-stone uppercase tracking-wider whitespace-nowrap ml-4" data-id="element-877">
                          {activity.time}
                        </span>
                      </div>
                      <p className="text-sm text-brand-stone" data-id="element-878">
                        {activity.detail}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <button className="w-full mt-6 py-3 text-sm font-bold text-brand-gold uppercase tracking-wider hover:bg-brand-cream rounded-lg transition-colors" data-id="element-879">
              View Full Log
            </button>
          </div>
        </ScrollReveal>
      </div>
    </div>;
}